export interface CloudEventLike {
  topic: string;
  payload: unknown;
}

export interface CloudEventInvalidation {
  refreshSessionSnapshot: boolean;
  sessionIds: string[];
  refreshNotes: boolean;
  refreshApiManager: boolean;
}

const noInvalidation: CloudEventInvalidation = {
  refreshSessionSnapshot: false,
  sessionIds: [],
  refreshNotes: false,
  refreshApiManager: false,
};

/**
 * Map a durable Cloud event to client cache invalidations. Event payloads are
 * hints only; every affected view re-reads its authoritative projection.
 */
export function routeCloudEvent(event: CloudEventLike): CloudEventInvalidation {
  if (event.topic.startsWith("note.")) {
    return { ...noInvalidation, refreshNotes: true };
  }
  if (event.topic === "relay.command.closed") {
    return { ...noInvalidation, refreshApiManager: true };
  }
  if (event.topic === "session.projection") {
    return {
      ...noInvalidation,
      refreshSessionSnapshot: true,
      sessionIds: cloudEventSessionIds(event),
    };
  }
  if (event.topic === "device.outbox.accepted") return noInvalidation;
  return { ...noInvalidation, refreshSessionSnapshot: true };
}

export function cloudEventSessionIds(event: CloudEventLike): string[] {
  const payload = eventPayloadRecord(event.payload);
  if (!payload) return [];
  const many = payload.sessionIds ?? payload.session_ids;
  if (Array.isArray(many)) {
    return [...new Set(many.filter(
      (value): value is string => typeof value === "string" && value.length > 0,
    ))];
  }
  const one = payload.sessionId ?? payload.session_id;
  return typeof one === "string" && one.length > 0 ? [one] : [];
}

/**
 * Cloud event envelopes have historically used both a direct payload and a
 * `data`/`event` wrapper while rolling out the event stream. Treat those
 * wrappers as transport detail so a message invalidation cannot be lost just
 * because one Cloud instance is on the other wire shape.
 */
function eventPayloadRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  for (const key of ["data", "event", "payload"]) {
    const nested = record[key];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const resolved = eventPayloadRecord(nested);
      if (resolved && (
        "sessionId" in resolved || "session_id" in resolved
        || "sessionIds" in resolved || "session_ids" in resolved
      )) return resolved;
    }
  }
  return record;
}
